/**
 * Planning session API — D-036-γ frontend data layer (β contract consumer).
 * Wire names: planningSessionId, createdByUserId, organizationId (canonical).
 */

import { api } from './client';
import { ENDPOINTS } from '../../config/api.config';
import { resolvePlanningSessionJobsiteId } from '../../components/command-center/shared/sheetLinkingUtils';

const BASE = ENDPOINTS.planningSessions.base;

export async function createPlanningSession({
  jobsiteId,
  initiationSurface = 'foreman_button',
  inputContext,
}) {
  const body = { jobsiteId, initiationSurface };
  if (inputContext !== undefined && inputContext !== null) {
    body.inputContext = inputContext;
  }
  return api.post(BASE, body);
}

export async function getPlanningSession(sessionId) {
  return api.get(`${BASE}/${sessionId}`);
}

/** Unwrap β paginated in-flight list `{ items, total, page, limit }` → `items[]`. */
export function unwrapPlanningSessionListPayload(raw) {
  const envelope = raw?.data ?? raw;
  if (Array.isArray(envelope)) return envelope;
  if (Array.isArray(envelope?.items)) return envelope.items;
  return [];
}

export async function listInFlightPlanningSessions({ jobsiteId, createdByUserId }) {
  const params = new URLSearchParams();
  if (jobsiteId) params.set('jobsiteId', jobsiteId);
  if (createdByUserId) params.set('createdByUserId', createdByUserId);
  const qs = params.toString();
  const res = await api.get(`${BASE}/in-flight${qs ? `?${qs}` : ''}`);
  const envelope = res?.data ?? res;
  const items = unwrapPlanningSessionListPayload(res);
  const meta =
    envelope && typeof envelope === 'object' && !Array.isArray(envelope)
      ? {
          total: envelope.total ?? items.length,
          page: envelope.page ?? 1,
          limit: envelope.limit ?? items.length,
        }
      : { total: items.length, page: 1, limit: items.length };
  return { ...res, data: items, meta };
}

export async function submitPlanningSessionAnswers(sessionId, payload) {
  return api.post(ENDPOINTS.planningSessions.answer(sessionId), payload);
}

/** Lane 2 conversational turn — POST /api/planning-sessions/:id/turns */
export async function submitPlanningSessionTurn(sessionId, { message }) {
  return api.post(ENDPOINTS.planningSessions.turns(sessionId), { message });
}

export async function savePlanningSessionManualEdits(sessionId, { edits }) {
  return api.post(ENDPOINTS.planningSessions.manualEdits(sessionId), { edits });
}

export async function decidePlanningSession(sessionId, payload) {
  return api.post(`${BASE}/${sessionId}/decide`, payload);
}

export async function materializePlanningSession(sessionId, payload = {}) {
  return api.post(ENDPOINTS.planningSessions.materialize(sessionId), payload);
}

/** Normalize API turn response (toolResults) to persisted conversationTurn shape. */
export function normalizeConversationTurnFromApi(turn) {
  if (!turn) return null;
  const toolCalls = (turn.toolResults || turn.toolCalls || []).map((tr) => ({
    toolName: tr.toolName,
    input: tr.input,
    success: tr.success,
    result: tr.data ?? tr.result ?? null,
    error: tr.error ?? null,
    durationMs: tr.durationMs,
  }));
  return {
    _id: turn.turnId ?? turn._id,
    role: turn.role ?? 'assistant',
    message: turn.message ?? '',
    toolCalls,
    reply: turn.reply ?? '',
    answersDelta: turn.answersDelta ?? [],
    planImpact: turn.planImpact ?? null,
    openQuestionRefs: turn.openQuestionRefs ?? [],
    createdAt: turn.createdAt ?? new Date().toISOString(),
  };
}

/** Extract clickable sheet refs from tool call results (compact cards). */
export function extractSheetRefsFromToolCalls(toolCalls = []) {
  const refs = [];
  const seen = new Set();
  const visit = (node) => {
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    const documentId = node.documentId ?? node.document_id;
    const sheetNumber =
      node.sheetNumber ?? node.sheet_number ?? node.sheetCode ?? node.code ?? node.sheetLabel;
    if (documentId || sheetNumber) {
      const key = `${documentId || ''}:${sheetNumber || ''}`;
      if (!seen.has(key)) {
        seen.add(key);
        refs.push({
          documentId: documentId != null ? String(documentId) : null,
          sheetNumber: sheetNumber != null ? String(sheetNumber) : null,
        });
      }
    }
    Object.values(node).forEach(visit);
  };
  for (const call of toolCalls) {
    visit(call.result ?? call.data);
    visit(call.input);
  }
  return refs;
}

/** Normalize API doc to consistent shape (β may return _id or planningSessionId). */
export function normalizePlanningSession(raw) {
  if (!raw) return null;
  const data = raw.data ?? raw;
  return {
    ...data,
    planningSessionId: data.planningSessionId ?? data._id?.toString?.() ?? data._id,
    jobsiteId: resolvePlanningSessionJobsiteId(data),
    status: data.status,
    expiresAt: data.expiresAt,
    proposedTaskDAG: data.proposedTaskDAG,
    refinedTaskDAG: data.refinedTaskDAG,
    finalTaskDAG: data.finalTaskDAG,
    conversationTurns: data.conversationTurns ?? [],
    heldPhases: data.heldPhases ?? null,
    materializedTasks: data.materializedTasks ?? [],
    manualEdits: data.manualEdits ?? [],
    inputContext: data.inputContext ?? null,
  };
}

/** Terminal statuses — stop polling. */
export const TERMINAL_PLANNING_STATUSES = new Set([
  'completed',
  'rejected',
  'draft',
  'expired',
  'approved',
  'discarded',
  'failed',
]);

/** User-facing copy for known planning session failure codes (LD-11). */
export const PLANNING_ERROR_MESSAGE_MAP = {
  LLM_TOTAL_DEGRADE:
    'The AI service was temporarily unavailable. This usually resolves on retry.',
  PLANNING_SESSION_THROTTLE:
    'Too many active planning sessions. Complete or discard an existing one first.',
  INVALID_STATE_TRANSITION:
    "This session can't be advanced from its current state. Start a new session to try again.",
  JOBSITE_NOT_FOUND: 'The jobsite for this session is no longer available.',
};

const PLANNING_ERROR_FALLBACK =
  'Plan generation failed. Please try again or contact support if this persists.';

/**
 * Map API question-round shape to BoltPlanningCard contract (P-010 boundary).
 * Defensive accept of both DB shape (questionId/prompt) and legacy SSE shape (id/text).
 */
export function mapSessionQuestionsToBoltCard(
  round,
  { planningSessionId, roundNumber }
) {
  const questions = (round?.questions ?? []).map((q) => {
    const options = q.context?.options ?? q.options ?? null;
    const inputType =
      q.context?.inputType ??
      q.inputType ??
      (options ? 'select' : 'text');
    return {
      id: q.questionId ?? q.id,
      text: q.prompt ?? q.text ?? '',
      options,
      inputType,
    };
  });
  return {
    planId: `ps-${planningSessionId}-r${roundNumber}`,
    context: null,
    questions,
  };
}

/**
 * Sanitize orchestrator lastError for foreman-facing headline (LD-11).
 */
export function sanitizePlanningError(lastError) {
  if (!lastError) return PLANNING_ERROR_FALLBACK;
  const code =
    lastError.code || lastError.message?.split(':')[0]?.trim() || '';
  if (PLANNING_ERROR_MESSAGE_MAP[code]) {
    return PLANNING_ERROR_MESSAGE_MAP[code];
  }
  const prefixMatch = Object.keys(PLANNING_ERROR_MESSAGE_MAP).find((key) =>
    code.startsWith(key)
  );
  if (prefixMatch) return PLANNING_ERROR_MESSAGE_MAP[prefixMatch];
  return PLANNING_ERROR_FALLBACK;
}

/** In-flight statuses for widget filter (β + γ TENTATIVE aliases). */
export const IN_FLIGHT_PLANNING_STATUSES = new Set([
  'created',
  'initializing',
  'gathering_context',
  'gathering-context',
  'questions_pending',
  'awaiting-foreman-input',
  'answers_received',
  'refining',
  'tasks_generated',
  'draft',
]);

/** Status where foreman may Approve | Edit | Discard (Brett-walked post-completion). */
export const DECISION_READY_STATUSES = new Set([
  'tasks_generated',
  'completed',
]);
